import { AlertTriangle, CheckCircle2, Clock3, History, RotateCcw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { SignalAuditEvent } from "@/lib/api";

export function WebhookAuditPanel({ events }: { events: SignalAuditEvent[] }) {
  return (
    <section className="mt-6 overflow-hidden rounded-md border border-border bg-muted/5">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <History className="size-4 text-muted-fg" />
          <h2 className="text-sm font-semibold">Recent webhook ingestion</h2>
        </div>
        <span className="text-xs text-muted-fg">{events.length} shown</span>
      </div>
      {events.length === 0 ? (
        <div className="px-4 py-8 text-sm text-muted-fg">
          No webhook ingestion audit events yet.
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {events.map((event) => (
            <WebhookAuditRow key={event.id} event={event} />
          ))}
        </div>
      )}
    </section>
  );
}

export function latestWebhookEventForConfig(
  events: SignalAuditEvent[],
  configId: string,
): SignalAuditEvent | undefined {
  return events.find((event) => metadataString(event.metadata, "webhook_config_id") === configId);
}

export function isWebhookAuditEvent(event: SignalAuditEvent): boolean {
  return (
    event.event_type === "webhook_ingestion_completed" ||
    metadataString(event.metadata, "ingest") === "webhook" ||
    metadataString(event.metadata, "webhook_config_id") !== null
  );
}

export function WebhookAuditSummary({ event }: { event: SignalAuditEvent | undefined }) {
  if (!event) {
    return <span className="text-[11px] text-muted-fg">no webhook activity yet</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-[11px] text-muted-fg">
      <AuditStatusIcon status={event.status} />
      <span className="text-fg">{event.status}</span>
      <span>{formatRelativeDate(event.created_at)}</span>
      {event.error_message ? <span className="text-red-300">{event.error_message}</span> : null}
    </span>
  );
}

function WebhookAuditRow({ event }: { event: SignalAuditEvent }) {
  const configId = metadataString(event.metadata, "webhook_config_id") ?? event.actor_id ?? "unknown";
  const provider = metadataString(event.metadata, "webhook_provider");
  const template = metadataString(event.metadata, "webhook_template_id");
  const inboxId = metadataString(event.metadata, "inbox_id");
  const symbols = metadataStringList(event.metadata, "detected_symbols");
  return (
    <div className="grid gap-2 px-4 py-3 text-xs md:grid-cols-[1fr_0.8fr_0.8fr_1.3fr] md:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <AuditStatusIcon status={event.status} />
          <span className="font-medium text-fg">{configId}</span>
          <Badge variant={event.status === "success" ? "success" : "outline"}>{event.status}</Badge>
        </div>
        <div className="mt-1 truncate text-muted-fg">
          {provider ? `${provider} · ` : ""}
          {formatDate(event.created_at)}
        </div>
      </div>
      <div className="text-muted-fg">
        <div>{event.items_saved ?? 0} saved</div>
        <div>{event.duplicates_skipped ?? 0} duplicate</div>
      </div>
      <div className="min-w-0 text-muted-fg">
        <div className="truncate">template: {template ?? "auto"}</div>
        <div className="truncate">inbox: {inboxId ?? "-"}</div>
      </div>
      <div className="min-w-0">
        {symbols.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {symbols.map((symbol) => (
              <Badge key={symbol} variant="outline">
                {symbol}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-fg">no symbols detected</span>
        )}
        {event.error_message ? (
          <div className="mt-1 truncate text-red-300">{event.error_message}</div>
        ) : null}
      </div>
    </div>
  );
}

function AuditStatusIcon({ status }: { status: string }) {
  if (status === "success") {
    return <CheckCircle2 className="size-3.5 text-emerald-300" />;
  }
  if (status === "duplicate") {
    return <RotateCcw className="size-3.5 text-sky-300" />;
  }
  if (status === "failed") {
    return <AlertTriangle className="size-3.5 text-red-300" />;
  }
  return <Clock3 className="size-3.5 text-muted-fg" />;
}

function metadataString(metadata: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = metadata?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function metadataStringList(metadata: Record<string, unknown> | null | undefined, key: string): string[] {
  const value = metadata?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

function formatRelativeDate(value: string): string {
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMinutes = Math.max(0, Math.round(diffMs / 60_000));
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.round(diffHours / 24)}d ago`;
}
